import React, { useEffect, useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { Tooltip } from '@mui/material';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { pageStyles } from '@/components/utils/PageLayout';
import { PageFrame, PageHeader, Surface, FilterBar, Button, Note, Pill, StatGrid } from '@/components/ui';
import { commonApi, portfolioPEApi } from '@/services/api';
import { formatCurrencyInCrores, formatDate, formatNumber } from '@/utils/formatters';
import { useToggleSet } from '@/hooks/useToggleSet';
import {
    joinPositions,
    isPooledViewKey,
    PORTFOLIO_PE_EXCLUSION_LABELS,
    PORTFOLIO_PE_VIEW_LABELS,
    PORTFOLIO_PE_VIEW_ORDER,
    POOLED_VIEW_KEY,
    PortfolioPECompanyRow,
    PortfolioPEData,
    PortfolioPEViewKey,
} from './types';
import styles from './PortfolioPE.module.css';

const RUPEES_PER_CRORE = 10_000_000;

const todayIso = (): string => new Date().toISOString().slice(0, 10);

/**
 * Rs Crore, rounded to 2 decimals via `formatCurrencyInCrores` (bare, so no "Cr" suffix
 * duplicates the column header). A value that would round to 0.00 but isn't actually
 * zero reads as "<0.01"/"-<0.01" instead, so a real (if tiny) figure doesn't look like
 * nothing, and a small negative figure doesn't read as a small positive one.
 */
const formatCroreGraceful = (crValue: number): string => {
    if (crValue === 0) return '0.00';
    if (Math.abs(crValue) < 0.005) return crValue < 0 ? '-<0.01' : '<0.01';
    return formatCurrencyInCrores(crValue * RUPEES_PER_CRORE, 'INR', true, 2);
};

const formatCrore = (value: string | null): string => {
    if (value === null) return '—';
    const num = parseFloat(value);
    return isNaN(num) ? '—' : formatCroreGraceful(num);
};

const formatPositionMV = (value: string): string => {
    const num = parseFloat(value);
    return isNaN(num) ? '—' : formatCroreGraceful(num / RUPEES_PER_CRORE);
};

/** A weight that rounds to 0.00% at 2 decimals but is not actually zero reads as
 * "excluded" — show "<0.01%" instead so a real (if tiny) position doesn't look like one
 * that was never counted. */
const formatWeightGraceful = (weight: string | null): string => {
    if (weight === null) return '—';
    const fraction = parseFloat(weight);
    if (isNaN(fraction)) return '—';
    if (fraction === 0) return '0.00%';
    const pct = fraction * 100;
    return Math.abs(pct) < 0.01 ? '<0.01%' : `${pct.toFixed(2)}%`;
};

const formatPE = (value: string | null): string =>
    value !== null ? formatNumber(parseFloat(value), 2) : '—';

/** A position market value of exactly zero (e.g. a fully closed-out lot still surfacing
 * in the fetched holdings) is classified `excluded` by the backend — see
 * `EXCLUSION_ZERO_POSITION_MV` in portfolio_pe_service.py — since it's not a real held
 * position. It still needs sinking to the bottom of the table on the frontend, since
 * there's no meaningful value to sort it by. */
const isZeroHolding = (row: PortfolioPECompanyRow): boolean => row.exclusion_reason === 'zero_position_mv';

const getSortValue = (row: PortfolioPECompanyRow, field: string): number | string => {
    switch (field) {
        case 'name':
            return row.name ?? '';
        case 'sector':
            return row.sector ?? '';
        case 'quarters_used':
            return row.quarters_used;
        case 'ttm_pat':
            return row.ttm_pat !== null ? parseFloat(row.ttm_pat) : -Infinity;
        case 'market_cap':
            return row.market_cap !== null ? parseFloat(row.market_cap) : -Infinity;
        case 'company_pe':
            return row.company_pe !== null ? parseFloat(row.company_pe) : -Infinity;
        case 'weight':
            return row.weight !== null ? parseFloat(row.weight) : -Infinity;
        case 'position_mv':
        default:
            return parseFloat(row.position_mv);
    }
};

const PortfolioPE: React.FC = () => {
    // Empty until the default resolves: today's holdings are only loaded the next day, so
    // defaulting to today would open on an empty page. See the effect below.
    const [date, setDate] = useState('');
    const [excludedIds, setExcludedIds] = useState<Set<number>>(new Set());
    const { selected: pendingExcludedIds, toggle: togglePending, replace: replacePending } = useToggleSet(excludedIds);
    const [data, setData] = useState<PortfolioPEData | null>(null);
    const [activeView, setActiveView] = useState<PortfolioPEViewKey>(POOLED_VIEW_KEY);
    // True from the first render: resolving the default date is part of the initial load.
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sortField, setSortField] = useState('position_mv');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    const [currentPage, setCurrentPage] = useState(1);

    const PAGE_SIZE = 20;

    // Default to the last working day (T-1, on the backend's market-holiday calendar).
    // A date the user picks before this resolves is kept rather than overwritten.
    useEffect(() => {
        let cancelled = false;
        commonApi.getLastWorkingDay()
            .then(({ last_working_day: lastWorkingDay }) => {
                if (!cancelled) setDate(prev => prev || lastWorkingDay || todayIso());
            })
            .catch(() => {
                if (!cancelled) setDate(prev => prev || todayIso());
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!date) return;
        let cancelled = false;
        setLoading(true);
        setError(null);

        portfolioPEApi.getPortfolioPE(date, Array.from(excludedIds))
            .then(response => {
                if (cancelled) return;
                if (response.meta?.access_restricted) {
                    setData(null);
                    setError(response.message ?? 'Data for this date requires complete access.');
                    return;
                }
                if (!response.data) {
                    setData(null);
                    setError(response.error ?? 'No data returned.');
                    return;
                }
                setData(response.data);
                setCurrentPage(1);
                replacePending(excludedIds);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                setData(null);
                const message = isAxiosError(err) ? err.response?.data?.error : undefined;
                setError(message ?? 'Failed to load portfolio P/E.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // replacePending's identity changes every render (useToggleSet isn't memoized) —
        // including it here would refetch on every render instead of only on date/excludedIds change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, excludedIds]);

    const setsEqual = (a: Set<number>, b: Set<number>): boolean =>
        a.size === b.size && [...a].every(id => b.has(id));

    const hasPendingChanges = !setsEqual(pendingExcludedIds, excludedIds);

    const handleApplyExclusions = () => setExcludedIds(new Set(pendingExcludedIds));
    const handleResetExclusions = () => replacePending(excludedIds);

    const handleSort = (field: string) => {
        setCurrentPage(1);
        if (field === sortField) {
            setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDir('desc');
        }
    };

    const activeCompanies = useMemo(
        () => joinPositions(data?.views[activeView], data?.companies ?? {}),
        [data, activeView],
    );

    const sortedCompanies = useMemo(() => {
        const sorted = [...activeCompanies].sort((a, b) => {
            const av = getSortValue(a, sortField);
            const bv = getSortValue(b, sortField);
            if (typeof av === 'string' || typeof bv === 'string') {
                return String(av).localeCompare(String(bv));
            }
            return av - bv;
        });
        if (sortDir === 'desc') sorted.reverse();
        // Zero-holding rows sink to the bottom regardless of sort field/direction —
        // there's no meaningful position to sort them by.
        const nonZero = sorted.filter(row => !isZeroHolding(row));
        const zero = sorted.filter(row => isZeroHolding(row));
        return [...nonZero, ...zero];
    }, [activeCompanies, sortField, sortDir]);

    const totalPages = Math.max(1, Math.ceil(sortedCompanies.length / PAGE_SIZE));
    const paginatedCompanies = useMemo(
        () => sortedCompanies.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
        [sortedCompanies, currentPage],
    );

    useEffect(() => {
        setCurrentPage(prev => Math.min(prev, totalPages));
    }, [totalPages]);

    const handleSelectView = (viewKey: PortfolioPEViewKey) => {
        setActiveView(viewKey);
        setCurrentPage(1);
    };

    const columns: ColumnDef<PortfolioPECompanyRow>[] = [
        {
            key: 'name', header: 'Company', sortable: true, width: '220px',
            render: row => {
                const label = row.symbol ?? row.name ?? `#${row.company_id}`;
                return row.name && row.symbol
                    ? (
                        <Tooltip title={row.name} arrow placement="top">
                            <span className={styles.nameWithSymbol}>{label}</span>
                        </Tooltip>
                    )
                    : <span>{label}</span>;
            },
        },
        {
            key: 'sector', header: 'Sector', sortable: true, width: '150px',
            render: row => row.sector ?? '—',
        },
        {
            key: 'basis', header: 'Basis', width: '120px',
            render: row => row.basis ?? '—',
        },
        {
            key: 'quarters_used', header: 'Quarters', sortable: true, width: '120px', align: 'right',
            tooltip: 'Trailing quarters used for TTM PAT, out of the number required',
            render: row => (
                <span>
                    {row.quarters_used}/{row.quarters_required}
                    {row.is_runrate && (
                        <Tooltip
                            title={`Estimated: only ${row.quarters_used} of ${row.quarters_required} quarters of actual data were available, so TTM PAT is run-rated (averaged and scaled up to a full ${row.quarters_required}-quarter figure) rather than an actual reported figure.`}
                            arrow placement="top"
                        >
                            <span className={styles.runRateBadge}>Est.</span>
                        </Tooltip>
                    )}
                </span>
            ),
        },
        {
            key: 'ttm_pat', header: 'TTM PAT (₹ Cr)', sortable: true, width: '140px', align: 'right',
            render: row => (
                <span className={row.is_runrate ? styles.runRateValue : undefined}>
                    {formatCrore(row.ttm_pat)}
                </span>
            ),
        },
        {
            key: 'market_cap', header: 'Market Cap (₹ Cr)', sortable: true, width: '150px', align: 'right',
            render: row => formatCrore(row.market_cap),
        },
        {
            key: 'company_pe', header: 'Company P/E', sortable: true, width: '110px', align: 'right',
            render: row => (
                <span className={row.is_runrate ? styles.runRateValue : undefined}>
                    {formatPE(row.company_pe)}
                </span>
            ),
        },
        {
            key: 'position_mv', header: 'Position MV (₹ Cr)', sortable: true, width: '140px', align: 'right',
            render: row => formatPositionMV(row.position_mv),
        },
        {
            key: 'weight', header: 'Weight', sortable: true, width: '90px', align: 'right',
            render: row => formatWeightGraceful(row.weight),
        },
        {
            key: 'include', header: 'Include', width: '150px',
            render: row => {
                if (row.exclusion_reason && row.exclusion_reason !== 'user_excluded') {
                    return (
                        <Pill tone="critical" title={PORTFOLIO_PE_EXCLUSION_LABELS[row.exclusion_reason]}>
                            {PORTFOLIO_PE_EXCLUSION_LABELS[row.exclusion_reason]}
                        </Pill>
                    );
                }
                const label = row.name ?? `#${row.company_id}`;
                return (
                    <label className={styles.excludeToggle}>
                        <input
                            type="checkbox"
                            checked={!pendingExcludedIds.has(row.company_id)}
                            onChange={() => togglePending(row.company_id)}
                            disabled={loading}
                            aria-label={`Include ${label}`}
                        />
                    </label>
                );
            },
        },
    ];

    const rowStyle = (row: PortfolioPECompanyRow): React.CSSProperties | undefined =>
        row.included ? undefined : { opacity: 0.6 };

    const emptyMessage = isPooledViewKey(activeView)
        ? 'No held companies found for the CORE strategy group on this date.'
        : `No held companies found for ${PORTFOLIO_PE_VIEW_LABELS[activeView]} on this date.`;

    const togglableCount = activeCompanies.filter(
        row => !row.exclusion_reason || row.exclusion_reason === 'user_excluded',
    ).length;

    return (
        <PageFrame
            header={
                <PageHeader
                    title="Portfolio P/E"
                    subtitle="Weighted harmonic P/E for the pooled CORE book and for each PMS/AIF/IFSC AIF view, as on the valuation date."
                />
            }
        >
            <StatGrid
                ariaLabel="Portfolio P/E view"
                items={PORTFOLIO_PE_VIEW_ORDER.map(viewKey => {
                    const view = data?.views[viewKey];
                    return {
                        id: viewKey,
                        label: PORTFOLIO_PE_VIEW_LABELS[viewKey],
                        value: formatPE(view?.portfolio_pe ?? null),
                        hint: view ? `${view.companies_included} included / ${view.companies_excluded} excluded` : '—',
                        loading,
                        onClick: () => handleSelectView(viewKey),
                        active: activeView === viewKey,
                    };
                })}
            />

            <FilterBar
                hideSearch
                summary={`Valuation date ${formatDate(date)}`}
                filters={
                    <div className={pageStyles['filter-group']}>
                        <label htmlFor="portfolio-pe-date">Valuation Date</label>
                        <input
                            id="portfolio-pe-date"
                            type="date"
                            value={date}
                            max={todayIso()}
                            onChange={e => setDate(e.target.value)}
                            className={pageStyles.input}
                        />
                    </div>
                }
            />

            {error && <Note tone="error">{error}</Note>}

            <Surface>
                <DataTable
                    embedded
                    data={paginatedCompanies}
                    columns={columns}
                    rowKey={row => row.company_id}
                    rowStyle={rowStyle}
                    totalCount={sortedCompanies.length}
                    loading={loading}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    pageSize={PAGE_SIZE}
                    sortField={sortField}
                    sortDir={sortDir}
                    onSort={handleSort}
                    hideSearch
                    emptyMessage={emptyMessage}
                    toolbarSlot={
                        <>
                            <span className={styles.toolbarInfo}>
                                {pendingExcludedIds.size} of {togglableCount} excluded
                                {hasPendingChanges && ' (unapplied changes)'}
                            </span>
                            <div className={styles.toolbarActions}>
                                <Button
                                    size="sm"
                                    onClick={handleResetExclusions}
                                    disabled={!hasPendingChanges || loading}
                                >
                                    Reset
                                </Button>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleApplyExclusions}
                                    disabled={!hasPendingChanges || loading}
                                >
                                    Apply
                                </Button>
                            </div>
                        </>
                    }
                />
            </Surface>
        </PageFrame>
    );
};

export default PortfolioPE;
